import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { routes } from "../routes";
import type { NavLink } from "../utils/NavLinks";
import { RoundedButton } from "./RoundedButton";

const BURGER_BREAKPOINT = 768;

const NavbarComponent = styled.nav`
    font-size: var(--size-smaller);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 42px 40px 42px 76px;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    box-sizing: border-box;
    z-index: 1000;

    > *:first-child {
        flex: 1;
        min-width: 0;
    }

    > *:nth-child(2) {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2rem;
    }

    > *:last-child {
        flex: 1;
        min-width: 0;
    }

    @media (max-width: ${BURGER_BREAKPOINT}px) {
        padding: 20px 16px;
    }
`;

const NavbarCenter = styled.div`
    @media (max-width: ${BURGER_BREAKPOINT}px) {
        display: none !important;
    }
`;

const NavbarLogoLink = styled(Link)`
    font-weight: 800;
    letter-spacing: -0.05rem;
    white-space: nowrap;
    flex-shrink: 0;
`;

const NavbarMiddle = styled.ul`
    list-style-type: none;
    padding: 0;
    margin: 0;
    font-weight: 300;
    position: relative;

    a:hover
    {
        opacity: 1 !important;
    }

    li:hover
    {
        filter: drop-shadow(0 0 7px rgba(var(--color-primary-desat-rgb),.3));
    }

    li
    {
        padding: .75rem 1.75rem;
        margin: 0 .5rem;
       float: left;
       position: relative;
       border-radius: 500px;
       transition: all .2s;

       a
       {
            width: 100%;
            height: 100%;
       }

       &:hover
       {
            background-color: var(--color-primary);
            color: #fff;
       }

       &.active, &.active:hover
       {
            background-color: var(--color-primary-dark);
            color: #fff;
       }

       &.disabled
       {
            opacity: .5;
            cursor: not-allowed;
            pointer-events: none;
            background: #f3f3f3;
            color: var(--color-primary-desat);
            border: 1px dashed var(--color-primary-desat);
       }

       &:hover > div {
           opacity: 1;
           pointer-events: all;
       }
    }
    
    li:has(> div) {
        &:hover::after {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            right: 0;
            height: 2px;
            background: transparent;
        }
    }

    @media (max-width: ${BURGER_BREAKPOINT}px) {
        display: none;
    }
`;

const NavbarLeft = styled.div`
    @media (max-width: ${BURGER_BREAKPOINT}px) {
        flex: 0 0 auto !important;
    }
`;

const NavbarLogoMobile = styled(Link)`
    font-weight: 800;
    letter-spacing: -0.05rem;
    white-space: nowrap;
    display: none;

    @media (max-width: ${BURGER_BREAKPOINT}px) {
        display: inline-flex;
        align-items: center;
    }
`;

const NavbarRight = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    visibility: hidden;

    @media (max-width: ${BURGER_BREAKPOINT}px) {
        visibility: visible;
        flex: 0 0 auto;

        a {
            display: none;
        }
    }
`;

const BurgerButton = styled.button`
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    flex-direction: column;
    justify-content: center;
    gap: 5px;

    span {
        display: block;
        width: 24px;
        height: 2px;
        background: var(--color-primary-dark);
        border-radius: 1px;
        transition: transform 0.2s;
    }

    @media (max-width: ${BURGER_BREAKPOINT}px) {
        display: flex;
    }
`;

const BurgerMenuOverlay = styled.div<{ $open: boolean }>`
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 1999;
    opacity: ${(p) => (p.$open ? 1 : 0)};
    pointer-events: ${(p) => (p.$open ? "all" : "none")};
    transition: opacity 0.2s;

    @media (max-width: ${BURGER_BREAKPOINT}px) {
        display: block;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
        position: absolute;
        width: 18px;
        height: 2px;
        background: var(--color-primary-dark);
        border-radius: 1px;
    }
    span:first-child { transform: rotate(45deg); }
    span:last-child { transform: rotate(-45deg); }
`;

const BurgerMenuPanel = styled.div<{ $open: boolean }>`
    display: none;
    position: fixed;
    top: 0;
    right: 0;
    width: min(280px, 85vw);
    height: 100vh;
    background: #fff;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
    z-index: 2000;
    padding: 64px 16px 20px;
    overflow-y: auto;
    transform: translateX(${(p) => (p.$open ? "0" : "100%")});
    transition: transform 0.25s ease;
    font-size: 0.9rem;

    @media (max-width: ${BURGER_BREAKPOINT}px) {
        display: block;
    }
`;

const BurgerMenuList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        padding: 0;
        margin: 0;
        float: none;
        border-radius: 0;
    }

    a, li {
        display: block;
        padding: 8px 12px;
        margin-bottom: 2px;
        border-radius: 6px;
        color: var(--color-dark-green);
        text-decoration: none;
        transition: background 0.15s;
        font-size: 0.9rem;
    }

    a:hover li, li:hover:not(.disabled) {
        background: rgba(var(--color-primary-rgb), 0.1);
    }

    li.active, a:has(> li.active) > li {
        background: var(--color-primary-dark);
        color: #fff;
    }

    li.disabled {
        font-weight: 600;
        font-size: 0.8rem;
        color: var(--color-primary);
        padding-top: 0.75rem;
        padding-bottom: 0.35rem;
        opacity: 1;
        cursor: default;
        pointer-events: none;
        background: transparent;
        border: none;
        margin-top: 0.35rem;
        border-top: 1px solid rgba(var(--color-primary-rgb), 0.15);
    }

    .submenu {
        padding-left: 12px;
        margin-top: 2px;
        font-size: 0.85rem;
    }
`;

const NavbarDropdownMenu = styled.div<{ $alignToEnd?: boolean }>`
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid rgba(var(--color-primary-rgb), 0.2);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: ${(p) => (p.$alignToEnd ? 0 : "calc(100% + 2px)")};
    left: ${(p) => (p.$alignToEnd ? "100%" : 0)};
    right: ${(p) => (p.$alignToEnd ? 0 : "unset")};
    text-align: left !important;
    min-width: 200px;
    padding: 8px 0;
    transition: all 0.2s ease;
    z-index: 2000;
    margin-top: 0;

    color: var(--color-dark-green);

    & > ul
    {
        display: flex;
        flex-direction: column;
        flex: 1;
        width: 100%;
        height: 100%;

        & > a
        {
            width: 100%;
            text-align: left !important;      
        }

        & li
        {
            --li-padding: 0.75rem 1.25rem;
            padding: var(--li-padding);
            margin: 0;
            width: 100%;
            border-radius: 0;
            transition: background-color 0.15s ease;
            box-sizing: border-box;
            overflow: hidden;
            color: var(--color-dark-green);
        }

        & > a:first-of-type > li
        {
            border-radius: 0;
        }

        & > a:last-of-type > li
        {
            border-radius: 0;
        }

        & > a:hover > li,
        & > li:hover:not(.disabled)
        {
            background-color: rgba(var(--color-primary-rgb), 0.08);
            color: var(--color-dark-green);
        }
        
        & > li.disabled
        {
            font-weight: 600;
            font-size: 0.9em;
            color: var(--color-primary);
            padding-top: 1rem;
            padding-bottom: 0.5rem;
            opacity: 1;
            cursor: default;
            pointer-events: none;
            background: transparent;
            border: none;
            margin-top: 0.5rem;
            border-top: 1px solid rgba(var(--color-primary-rgb), 0.15);
        }
        
        & > li.disabled:first-child
        {
            padding-top: 0.5rem;
            margin-top: 0;
            border-top: none;
        }
    }
`;

const NavbarDropdownLink = styled.li`
    border-radius: 500px !important;

    & span
    {
        display: inline-flex;
        align-items: center;
    }
`;

interface NavbarProps {
  links: NavLink[];
  isSubmenu?: boolean;
  doubleClickFallback?: string;
}

const DropdownTitle = styled.span`
    display: block;
    position: relative;
    width: 100%;
`;

const DropdownArrow = styled.span<{ $header?: boolean }>`
    display: inline-block;
    margin-left: 8px;
    vertical-align: middle;
    line-height: 1;
    
    &::after {
        content: '';
        display: inline-block;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 5px solid currentColor;
        transition: transform 0.2s ease;
        transform: rotate(0deg);
    }
    
    ${(p) =>
      p.$header
        ? `
        li:hover &::after {
            transform: rotate(180deg);
        }
    `
        : ""}
`;

const NavbarDropdown: React.FC<NavbarProps> = ({ links, isSubmenu }) => {
  const _location = useLocation();

  return (
    <NavbarDropdownMenu $alignToEnd={isSubmenu}>
      <ul>
        {links.map((link) => (
          <React.Fragment key={link.name}>
            {Array.isArray(link.to) ? (
              <li>
                <DropdownTitle>
                  {link.name}
                  <DropdownArrow />
                </DropdownTitle>
                <NavbarDropdown links={link.to} isSubmenu={true} />
              </li>
            ) : link.disabled ? (
              <li className="disabled" title={link.tooltip || "Coming soon"}>
                {link.name}
              </li>
            ) : link.newTab || link.external ? (
              <a
                href={link.to}
                {...(link.newTab
                  ? { target: "_blank", rel: "noreferrer" }
                  : {})}
              >
                <li>{link.name}</li>
              </a>
            ) : (
              <Link to={link.to}>
                <li>{link.name}</li>
              </Link>
            )}
          </React.Fragment>
        ))}
      </ul>
    </NavbarDropdownMenu>
  );
};

const Navbar: React.FC<NavbarProps> = ({ links, doubleClickFallback = "" }) => {
  var location = useLocation();
  const [burgerOpen, setBurgerOpen] = useState(false);

  const getActiveClass = (linkTo: string | NavLink[]): string => {
    if (Array.isArray(linkTo)) {
      const anyActive = (items: NavLink[]): boolean =>
        items.some((item) => {
          if (Array.isArray(item.to)) return anyActive(item.to);
          return getActiveClass(item.to as string) === "active";
        });
      return anyActive(linkTo) ? "active" : "";
    }

    if (linkTo === routes.schema.absolute) {
      return location.pathname.startsWith(routes.schema.absolute)
        ? "active"
        : "";
    }

    // Tools is active when on dashboard or annotation/publication tools (but NOT schema)
    if (linkTo === routes.dashboard) {
      const isOnSchema = location.pathname.startsWith(routes.schema.absolute);
      if (isOnSchema) {
        return ""; // Schema takes precedence
      }
      const isOnDashboard =
        location.pathname === routes.dashboard ||
        location.pathname.startsWith(routes.annotationTools.absolute) ||
        location.pathname.startsWith(routes.publicationTools.absolute);
      return isOnDashboard ? "active" : "";
    }

    return location.pathname === linkTo ? "active" : "";
  };

  const isLinkActive = (linkTo: string | NavLink[]): boolean => {
    if (Array.isArray(linkTo)) {
      return false;
    }

    // Schema is active when on schema page
    if (linkTo === routes.schema.absolute) {
      return location.pathname.startsWith(routes.schema.absolute);
    }

    // Tools is active when on dashboard or annotation/publication tools (but NOT schema)
    if (linkTo === routes.dashboard) {
      const isOnSchema = location.pathname.startsWith(routes.schema.absolute);
      if (isOnSchema) {
        return false; // Schema takes precedence
      }
      const isOnDashboard =
        location.pathname === routes.dashboard ||
        location.pathname.startsWith(routes.annotationTools.absolute) ||
        location.pathname.startsWith(routes.publicationTools.absolute);
      return isOnDashboard;
    }

    return location.pathname === linkTo;
  };

  const renderLinks = (links: NavLink[], _isSubmenu: boolean) => {
    const content = (
      <ul>
        {links.map((link) => (
          <React.Fragment key={link.name}>
            {Array.isArray(link.to) ? (
              <NavbarDropdownLink className={getActiveClass(link.to)}>
                <span>
                  {link.name}
                  <DropdownArrow $header />
                </span>
                <NavbarDropdown links={link.to} />
              </NavbarDropdownLink>
            ) : link.disabled ? (
              <li
                className={`disabled ${getActiveClass(link.to)}`}
                title={link.tooltip || "Coming soon"}
              >
                {link.name}
              </li>
            ) : link.newTab || link.external ? (
              <a
                href={link.to as string}
                {...(link.newTab
                  ? { target: "_blank", rel: "noreferrer" }
                  : {})}
              >
                <li className={getActiveClass(link.to)}>{link.name}</li>
              </a>
            ) : isLinkActive(link.to) ? (
              <li
                className={`active ${getActiveClass(link.to)}`}
                style={{ cursor: "default", pointerEvents: "none" }}
              >
                {link.name}
              </li>
            ) : (
              <Link
                to={
                  location.pathname === link.to ? doubleClickFallback : link.to
                }
              >
                <li className={getActiveClass(link.to)}>{link.name}</li>
              </Link>
            )}
          </React.Fragment>
        ))}
      </ul>
    );
    return content;
  };

  const renderBurgerLinks = (links: NavLink[]) => (
    <>
      {links.map((link) => (
        <React.Fragment key={link.name}>
          {Array.isArray(link.to) ? (
            <>
              <li className="disabled">{link.name}</li>
              <ul className="submenu">
                {link.to.map((sub) =>
                  Array.isArray(sub.to) ? (
                    <React.Fragment key={sub.name}>
                      <li className="disabled">{sub.name}</li>
                      <ul className="submenu">
                        {(sub.to as NavLink[]).map((subsub) =>
                          subsub.disabled ? (
                            <li
                              key={subsub.name}
                              className="disabled"
                              title={subsub.tooltip}
                            >
                              {subsub.name}
                            </li>
                          ) : subsub.newTab || subsub.external ? (
                            <a
                              key={subsub.name}
                              href={subsub.to as string}
                              {...(subsub.newTab
                                ? { target: "_blank", rel: "noreferrer" }
                                : {})}
                            >
                              <li>{subsub.name}</li>
                            </a>
                          ) : (
                            <Link
                              key={subsub.name}
                              to={subsub.to as string}
                              onClick={() => setBurgerOpen(false)}
                            >
                              <li className={getActiveClass(subsub.to)}>
                                {subsub.name}
                              </li>
                            </Link>
                          ),
                        )}
                      </ul>
                    </React.Fragment>
                  ) : sub.disabled ? (
                    <li key={sub.name} className="disabled" title={sub.tooltip}>
                      {sub.name}
                    </li>
                  ) : sub.newTab || sub.external ? (
                    <a
                      key={sub.name}
                      href={sub.to}
                      {...(sub.newTab
                        ? { target: "_blank", rel: "noreferrer" }
                        : {})}
                    >
                      <li>{sub.name}</li>
                    </a>
                  ) : (
                    <Link
                      key={sub.name}
                      to={sub.to}
                      onClick={() => setBurgerOpen(false)}
                    >
                      <li className={getActiveClass(sub.to)}>{sub.name}</li>
                    </Link>
                  ),
                )}
              </ul>
            </>
          ) : link.disabled ? (
            <li className="disabled" title={link.tooltip}>
              {link.name}
            </li>
          ) : link.newTab || link.external ? (
            <a
              href={link.to}
              {...(link.newTab
                ? { target: "_blank", rel: "noreferrer" }
                : {})}
            >
              <li>{link.name}</li>
            </a>
          ) : (
            <Link to={link.to} onClick={() => setBurgerOpen(false)}>
              <li className={getActiveClass(link.to)}>{link.name}</li>
            </Link>
          )}
        </React.Fragment>
      ))}
    </>
  );

  const logoContent = (
    <>
      <img
        src="/logo.svg"
        style={{
          verticalAlign: "middle",
          marginBottom: "8px",
          filter: "var(--filter-primary)",
          marginRight: "8px",
        }}
        width="26"
        height="26"
        alt=""
      />
      DATADESC
    </>
  );

  return (
    <>
      <NavbarComponent>
        <NavbarLeft>
          <NavbarLogoMobile to={routes.home}>{logoContent}</NavbarLogoMobile>
        </NavbarLeft>
        <NavbarCenter>
          <NavbarLogoLink to={routes.home}>{logoContent}</NavbarLogoLink>
          <NavbarMiddle>{renderLinks(links, false)}</NavbarMiddle>
        </NavbarCenter>
        <NavbarRight>
          <BurgerButton
            onClick={() => setBurgerOpen(true)}
            aria-label="Open menu"
          >
            <span />
            <span />
            <span />
          </BurgerButton>
          <RoundedButton to="##" $secondary>
            Login
          </RoundedButton>
          <RoundedButton to="##">Register</RoundedButton>
        </NavbarRight>
      </NavbarComponent>

      <BurgerMenuOverlay
        $open={burgerOpen}
        onClick={() => setBurgerOpen(false)}
      />
      <BurgerMenuPanel $open={burgerOpen}>
        <CloseButton
          onClick={() => setBurgerOpen(false)}
          aria-label="Close menu"
        >
          <span />
          <span />
        </CloseButton>
        <BurgerMenuList>{renderBurgerLinks(links)}</BurgerMenuList>
      </BurgerMenuPanel>
    </>
  );
};

export default Navbar;
